import { type Ref, ref, shallowReadonly } from 'vue';
import { Store } from './Store';
import type { StoreStatus } from './StoreStatus';

export abstract class StoreMap<TKey, TValue> extends Store {
  private readonly data = new Map<TKey, Ref<TValue | null>>();
  private readonly status = new Map<TKey, Ref<StoreStatus>>();
  private readonly lastLoaded = new Map<TKey, number>();
  private readonly subscribers = new Map<TKey, Array<(value: TValue) => void>>();

  constructor(cacheMs = 10_000) {
    super(cacheMs); // inherit cacheMs and userStore from Store
  }

  protected getOrInitRef<K, V>(map: Map<K, Ref<V>>, key: K, fallback: V): Ref<V> {
    if (!map.has(key)) {
      map.set(key, ref(fallback) as Ref<V>);
    }
    return map.get(key)!;
  }

  protected isCacheValid(key: TKey): boolean {
    const ts = this.lastLoaded.get(key);
    return ts !== undefined && Date.now() - ts < this.CacheMs;
  }

  Invalidate(key: TKey): void {
    this.lastLoaded.delete(key);
  }

  async ForceReload(key: TKey): Promise<TValue | null> {
    this.Invalidate(key);
    return await this.Get(key, true);
  }

  async Get(key: TKey, force = false): Promise<TValue | null> {
    this.EnsureLoggedOn(); // required login check

    const dataRef = this.getOrInitRef(this.data, key, null);
    const statusRef = this.getOrInitRef(this.status, key, 'idle');

    if (!force && this.isCacheValid(key) && dataRef.value !== null) {
      return dataRef.value;
    }

    statusRef.value = 'loading';

    try {
      const loaded = await this.Fetch(key);
      dataRef.value = loaded;
      this.lastLoaded.set(key, Date.now());
      statusRef.value = 'ready';

      const cbs = this.subscribers.get(key);
      if (cbs) cbs.forEach((cb) => cb(loaded));

      return loaded;
    } catch (e) {
      console.error(`[${this.constructor.name}] Load failed for`, key, ':', e);
      dataRef.value = null;
      statusRef.value = 'error';
      throw e;
    }
  }

  Subscribe(key: TKey): {
    data: Readonly<Ref<TValue | null>>;
    status: Readonly<Ref<StoreStatus>>;
  } {
    return {
      data: shallowReadonly(this.getOrInitRef(this.data, key, null)),
      status: shallowReadonly(this.getOrInitRef(this.status, key, 'idle')),
    };
  }

  OnUpdate(key: TKey, callback: (value: TValue) => void): void {
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, []);
    }
    this.subscribers.get(key)!.push(callback);
  }

  OffUpdate(key: TKey, callback: (value: TValue) => void): void {
    const subs = this.subscribers.get(key);
    if (subs) {
      this.subscribers.set(
        key,
        subs.filter((cb) => cb !== callback),
      );
    }
  }

  abstract Fetch(key: TKey): Promise<TValue>;
}
